// Diagnostic records deliberately exclude bodies, query strings, headers and error
// messages: all can contain credentials, customer data or device configuration.
const LIMIT = 500;
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const records = [];
const SAFE_FIELDS = new Set([
  "operation",
  "method",
  "endpoint",
  "status",
  "duration_ms",
  "request_id",
  "gateway_request_id",
  "error_type",
  "error_code",
  "source",
  "line",
  "column",
  "reason",
  "count",
  "failed_count",
  "server_request_id",
]);

const identifier = (value) =>
  typeof value === "string" && /^[a-zA-Z0-9_.:/-]{1,160}$/.test(value) ? value : undefined;

export const newDiagnosticId = () =>
  window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

// API routes have static first segments; never retain path parameters or a host.
export function diagnosticEndpoint(url = "") {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/^\/beta\/([a-zA-Z][a-zA-Z0-9_-]*)/);
    return match ? `/beta/${match[1]}` : "other";
  } catch {
    return "unknown";
  }
}

export function recordDiagnostic(event, fields = {}, level = "info") {
  // Logging must never prevent the operation being diagnosed.
  try {
    const record = {
      timestamp: new Date().toISOString(),
      component: "portal-ui",
      version: process.env.REACT_APP_VERSION,
      level,
      event: identifier(event) || "unknown",
    };
    Object.entries(fields).forEach(([key, value]) => {
      if (!SAFE_FIELDS.has(key)) return;
      if (typeof value === "number" && Number.isFinite(value)) record[key] = value;
      else if (identifier(value)) record[key] = value;
    });
    records.push(record);
    while (records.length > LIMIT || Date.parse(records[0].timestamp) < Date.now() - MAX_AGE_MS) records.shift();
    if (level === "error") console.error(JSON.stringify(record)); // eslint-disable-line no-console
    else if (level === "warn") console.warn(JSON.stringify(record)); // eslint-disable-line no-console
    return record;
  } catch {
    return undefined;
  }
}

export function reportError(operation, error, fields = {}) {
  // Keep the build file and position for source-map lookup, without the error
  // message, absolute URL, user-supplied query string, or stack arguments.
  const position =
    typeof error?.stack === "string" ? error.stack.match(/\/([a-zA-Z0-9_.-]+\.jsx?):(\d+):(\d+)\)?(?:\n|$)/) : null;
  return recordDiagnostic(
    "operation.failed",
    {
      source: position?.[1],
      line: position ? Number(position[2]) : undefined,
      column: position ? Number(position[3]) : undefined,
      request_id: error?.config?.diagnostics?.request_id,
      server_request_id:
        error?.headers?.["x-megaportal-request-id"] || error?.response?.headers?.["x-megaportal-request-id"],
      ...fields,
      operation,
      error_type: error instanceof Error ? error.name : "HandledError",
      error_code: identifier(error?.code),
      status: error?.response?.status || error?.status,
    },
    "error",
  );
}

export async function settleDiagnosticBatch(operation, tasks) {
  const started = Date.now();
  const results = await Promise.allSettled(tasks);
  const failed = results.filter((result) => result.status === "rejected").length;
  recordDiagnostic(
    "batch.completed",
    {
      operation,
      count: results.length,
      failed_count: failed,
      duration_ms: Date.now() - started,
    },
    failed ? "warn" : "info",
  );
  return results;
}

export function diagnosticSnapshot() {
  return records
    .filter((record) => Date.parse(record.timestamp) >= Date.now() - MAX_AGE_MS)
    .map((record) => ({ ...record }));
}

export function downloadDiagnostics() {
  const blob = new Blob([JSON.stringify(diagnosticSnapshot(), null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `megaportal-diagnostics-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function installGlobalDiagnostics() {
  const onError = (event) =>
    reportError("browser.uncaught", event.error, {
      source: event.filename?.split("/").pop()?.split("?")[0],
      line: event.lineno,
      column: event.colno,
    });
  const onRejection = (event) => reportError("browser.unhandled_rejection", event.reason);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  recordDiagnostic("application.started");
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
